import type { BillableEventKind, LabStatementSummary } from '@dru/types';
import { BILLABLE_EVENT_LABEL, formatPaise } from '@dru/types';
import { Alert } from '@dru/ui';

export interface LabStatementLine {
  id?: string;
  event: BillableEventKind;
  label: string;
  quantity: number;
  ratePaise: number;
  amountPaise: number;
  companyName?: string | null;
  profileId?: string | null;
  profile?: { tests: Array<{ test: { code: string; name: string } }> } | null;
  sellingUnitPaise?: number | null;
  sellingAmountPaise?: number | null;
  sellingBasis?: string | null;
}

/**
 * A partner lab statement as the platform reads it: what was billed, at the
 * agreed lab rate, what the lab is owed, what the customer is charged for the
 * same items, and the gross margin.
 *
 * Platform-only. The lab's own view never carries the selling side.
 */
export function LabStatementTable({ lines, summary, currency = 'INR' }: {
  lines: LabStatementLine[];
  summary?: LabStatementSummary | null;
  currency?: string;
}) {
  const money = (p: number | null | undefined) => (p == null ? '—' : formatPaise(p, currency));
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded border border-neutral-100">
        <table className="w-full min-w-[40rem] text-sm">
          <thead className="bg-neutral-50 text-left text-caption font-medium uppercase tracking-wide text-neutral-500">
            <tr>
              <th scope="col" className="px-3 py-2">Billed item</th>
              <th scope="col" className="px-3 py-2 text-right">Qty</th>
              <th scope="col" className="px-3 py-2 text-right">Agreed lab rate</th>
              <th scope="col" className="px-3 py-2 text-right">Payable to lab</th>
              <th scope="col" className="px-3 py-2 text-right">Customer price</th>
              <th scope="col" className="px-3 py-2 text-right">Selling amount</th>
              <th scope="col" className="px-3 py-2 text-right">Gross margin</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {lines.map((l, i) => {
              const margin = l.sellingAmountPaise == null ? null : l.sellingAmountPaise - l.amountPaise;
              return (
                <tr key={l.id ?? i} className="align-top">
                  <td className="px-3 py-2">
                    <span className="text-neutral-900">{l.label}</span>
                    <span className="block text-caption text-neutral-500">
                      {l.profileId ? 'Profile price' : BILLABLE_EVENT_LABEL[l.event] ?? l.event}
                      {l.companyName ? ` · ${l.companyName}` : ''}
                    </span>
                    {l.profile && l.profile.tests.length > 0 && (
                      <span className="mt-0.5 block text-caption text-neutral-500">
                        Covers: {l.profile.tests.map((t) => t.test.name).join(', ')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{l.quantity}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(l.ratePaise)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(l.amountPaise)}</td>
                  <td className="px-3 py-2 text-right tabular-nums" title={l.sellingBasis ?? undefined}>
                    {money(l.sellingUnitPaise)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(l.sellingAmountPaise)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${margin != null && margin < 0 ? 'text-danger-600' : ''}`}>
                    {money(margin)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {summary && (
        <dl className="grid gap-2 rounded border border-neutral-100 bg-neutral-50 p-3 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-caption text-neutral-500">Total payable to lab</dt>
            <dd className="tabular-nums text-base font-semibold text-neutral-900">{money(summary.payablePaise)}</dd>
          </div>
          <div>
            <dt className="text-caption text-neutral-500">Customer selling amount</dt>
            <dd className="tabular-nums text-base font-semibold text-neutral-900">{money(summary.sellingPaise)}</dd>
          </div>
          <div>
            <dt className="text-caption text-neutral-500">Gross margin (before other costs and taxes)</dt>
            <dd className={`tabular-nums text-base font-semibold ${summary.grossMarginPaise < 0 ? 'text-danger-600' : 'text-neutral-900'}`}>
              {money(summary.grossMarginPaise)}
            </dd>
          </div>
        </dl>
      )}
      {summary && summary.linesWithoutSelling > 0 && (
        <Alert tone="warning">
          {summary.linesWithoutSelling} line{summary.linesWithoutSelling === 1 ? ' has' : 's have'} no item selling price
          ({money(summary.payableWithoutSellingPaise)} payable). Attendance fees are charged in the package price; for tests and
          profiles, add a customer price on the company's or the platform's rate card. These lines are left out of the margin.
        </Alert>
      )}
    </div>
  );
}
